import * as React from 'react';

import { MultiSelectFilter, cn, Badge } from '@rematter/ui-web';

import { useTranslations } from '../i18n/useTranslations';
import { createQueryHook } from '../providers/api/createQueryHook';

const useSupplierAccounts = createQueryHook(
  'shared.supplierAccounts',
  (params: {
    accountIds?: string[];
    searchTerm?: string;
    offset?: number;
    limit?: number;
  }) => ({
    accounts: {
      __args: {
        filter: {
          accountIds:
            params.accountIds?.length === 0
              ? ['invalid-id']
              : params.accountIds,
          searchTerm: params.searchTerm || undefined,
          isSupplier: true,
        },
        pagination: {
          offset: params.offset ?? 0,
          limit: params.limit ?? 50,
        },
        sorting: ['NameAsc'],
      },
      page: {
        __scalar: true,
      },
      entries: {
        id: true,
        name: true,
        type: true,
      },
    },
  }),
  {
    select: (data) =>
      data?.accounts.entries.map((entry) => ({
        id: entry.id,
        name: entry.name,
        type: entry.type,
      })) ?? [],
  },
);

interface Account {
  id: string;
  name: string;
  type?: string;
}

interface SupplierFilterProps {
  selectedSupplierIds: Set<React.Key>;
  onSelectionChange: (supplierIds: Set<React.Key>) => void;
  onReset: () => void;
  onClose?: () => void;
  className?: string;
}

export const SupplierFilter: React.FC<SupplierFilterProps> = ({
  selectedSupplierIds,
  onSelectionChange,
  onReset,
  onClose,
  className,
}) => {
  const t = useTranslations();

  const [searchTerm, setSearchTerm] = React.useState<string | undefined>(
    undefined,
  );

  const {
    data: suppliers,
    isFetching: isFetchingSuppliers,
    isLoading: isLoadingSuppliers,
  } = useSupplierAccounts({
    searchTerm,
    limit: 20,
  });

  const { data: selectedSuppliers } = useSupplierAccounts({
    accountIds: Array.from(selectedSupplierIds) as string[],
  });

  const handleSearch = React.useCallback((query: string) => {
    setSearchTerm(query);
  }, []);

  const getLabel = React.useCallback(
    (item: Account, isSelected: boolean) => (
      <div className="flex w-full items-center gap-2">
        <div
          className={cn(
            'w-0 grow truncate text-sm text-neutral/60',
            isSelected && 'text-neutral',
          )}
        >
          {item.name}
        </div>
        {item.type && (
          <Badge
            color={item.type === 'Individual' ? 'warning' : 'primary'}
            className="ml-auto"
          >
            {item.type}
          </Badge>
        )}
      </div>
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className={cn('@SupplierFilter', 'min-w-[350px]', className)}
      entries={suppliers}
      selectedEntries={selectedSuppliers}
      selectedKeys={selectedSupplierIds}
      onSelectionChange={onSelectionChange}
      getEntryKey={(supplier) => supplier.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={onReset}
      searchPlaceholder={t.filters.supplier.placeholder}
      isLoading={isLoadingSuppliers}
      isFetching={isFetchingSuppliers}
    />
  );
};
